import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, ImportWizard } from './ui';
import {
  C360Table, ColumnChooser, getOperator,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/trainings';

const CATEGORIES = [
  { value: 'ONBOARDING', label: 'Oryantasyon' },
  { value: 'PRODUCT', label: 'Ürün' },
  { value: 'SALES', label: 'Satış' },
  { value: 'SAFETY', label: 'Güvenlik' },
  { value: 'COMPLIANCE', label: 'Uyum' },
  { value: 'SOFT_SKILLS', label: 'Yetkinlik' },
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  title: { type: 'text', odataField: 'Title' },
  category: { type: 'select', odataField: 'Category', options: CATEGORIES },
  durationSec: { type: 'number', odataField: 'DurationSec' },
  rewardPoints: { type: 'number', odataField: 'RewardPoints' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['textId', 'title', 'description', 'youtubeVideoId'];

const PAGE_SIZE_DEFAULT = 10;

function unwrapResponse(payload) {
  const data = payload?.Data ?? payload?.data ?? payload;
  return {
    items: data?.items ?? data?.Items ?? [],
    activeCount: data?.activeCount ?? data?.ActiveCount ?? 0,
    inactiveCount: data?.inactiveCount ?? data?.InactiveCount ?? 0,
  };
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDuration(sec) {
  const n = Number(sec);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const m = Math.floor(n / 60);
  const r = n % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Client-side filtre predikatı (backend OData değil; veri tek seferde ≤500 çekilir).
function matchColumnFilter(meta, filter, raw) {
  if (!filter || !meta) return true;
  const op = getOperator(meta.type, filter.op);
  if (!op) return true;
  const value = filter.value;
  const value2 = filter.value2;

  if (op.id === 'blank') return raw === null || raw === undefined || raw === '';
  if (op.id === 'notBlank') return !(raw === null || raw === undefined || raw === '');
  if (op.needsValue && (value === undefined || value === null || value === '')) return true;

  if (meta.type === 'text') {
    const hay = String(raw ?? '').toLocaleLowerCase('tr-TR');
    const needle = String(value).toLocaleLowerCase('tr-TR');
    switch (op.id) {
      case 'contains': return hay.includes(needle);
      case 'notContains': return !hay.includes(needle);
      case 'startsWith': return hay.startsWith(needle);
      case 'endsWith': return hay.endsWith(needle);
      case 'equals': return hay === needle;
      case 'notEquals': return hay !== needle;
      default: return true;
    }
  }
  if (meta.type === 'number') {
    const n = Number(raw);
    const v = Number(value);
    switch (op.id) {
      case 'equals': return n === v;
      case 'notEquals': return n !== v;
      case 'gt': return n > v;
      case 'gte': return n >= v;
      case 'lt': return n < v;
      case 'lte': return n <= v;
      case 'between': return n >= v && n <= Number(value2);
      default: return true;
    }
  }
  if (meta.type === 'select') {
    if (meta.valueFormat === 'raw') return String(!!raw) === String(value);
    return String(raw ?? '') === String(value);
  }
  return true;
}

export function TrainingsPage() {
  usePageMeta('Eğitimler', 'Saha temsilcisine atanan eğitim videoları');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, inactiveCount: 0 });
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const [sortField, setSortField] = useState('PublishedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=500`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ activeCount: data.activeCount, inactiveCount: data.inactiveCount });
    } catch (err) {
      showToast('error', err.message || 'Veri alınırken hata oluştu');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const applyFilterText = useCallback((text) => { setFilterText(text); setCurrentPage(1); }, []);
  const setColumnFilter = useCallback((columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  }, []);
  const clearColumnFilter = useCallback((columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  }, []);

  const applySort = useCallback((field, dir) => {
    setSortField(field || 'PublishedAt');
    setSortDir(dir || 'asc');
    setCurrentPage(1);
  }, []);

  // === Client-side filtre + sıralama ===
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const matchText = (row) => {
      if (!needle) return true;
      return SEARCH_FIELDS.some((f) => {
        const v = row[f];
        return v && String(v).toLocaleLowerCase('tr-TR').includes(needle);
      });
    };
    const matchColumns = (row) => Object.entries(columnFilters).every(([columnId, filter]) => {
      const meta = COLUMN_FILTER_META[columnId];
      return matchColumnFilter(meta, filter, row[columnId]);
    });
    const filtered = rows.filter((r) => matchText(r) && matchColumns(r));

    const gridField = toGridSortField(sortField);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortVal = (row) => {
      switch (gridField) {
        case 'title': return String(row.title || '');
        case 'textId': return String(row.textId || '');
        case 'category': return String(row.category || '');
        case 'durationSec': return Number(row.durationSec) || 0;
        case 'rewardPoints': return Number(row.rewardPoints) || 0;
        case 'isActive': return row.isActive ? 1 : 0;
        case 'publishedAt':
        default: {
          const v = row.publishedAt || row.createdDate;
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortVal(a); const bv = sortVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  // === Detay navigasyonu (tam-sayfa /trainings/:id) ===
  const openNew = () => navigate('/trainings/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/trainings/${row.id}`, { state: { row } }); };
  // #282 kalem → düzenleme modu (DetailShell ?edit=1 ile açılır).
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/trainings/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/${selected.id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'Kayıt silinemedi');
      showToast('success', 'Eğitim silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('title', { header: 'Başlık', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('category', {
      header: 'Kategori', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return CATEGORY_LABEL[v] || v || '-'; },
    }),
    columnHelper.accessor('durationSec', {
      header: 'Süre', meta: { filterType: 'number' },
      cell: (info) => formatDuration(info.getValue()),
    }),
    columnHelper.accessor('rewardPoints', {
      header: 'Puan', meta: { filterType: 'number' },
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor('publishedAt', {
      header: 'Yayın', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('PublishedAt', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { field: 'textId', headerName: 'Kod' },
        { field: 'title', headerName: 'Başlık' },
        { headerName: 'Kategori', valueGetter: ({ row }) => CATEGORY_LABEL[row.category] || row.category || '' },
        { headerName: 'Süre', valueGetter: ({ row }) => formatDuration(row.durationSec) },
        { field: 'rewardPoints', headerName: 'Puan' },
        { headerName: 'Yayın', valueGetter: ({ row }) => formatDate(row.publishedAt) },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `egitimler_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma ===
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const parseCategory = (v) => {
    const raw = String(v ?? '').trim();
    if (CATEGORY_LABEL[raw.toUpperCase()]) return raw.toUpperCase();
    const found = CATEGORIES.find((c) => c.label.toLowerCase() === raw.toLowerCase());
    return found ? found.value : 'PRODUCT';
  };
  const parseBool = (v) => !['hayir', 'hayır', 'false', '0', 'no', 'pasif'].includes(String(v ?? '').trim().toLowerCase());
  const importRowToPayload = (row) => ({
    textId: importPick(row, ['Kod', 'TextId', 'textId']),
    title: importPick(row, ['Başlık', 'Baslik', 'Title', 'title']),
    description: importPick(row, ['Açıklama', 'Aciklama', 'Description', 'description']) || null,
    youtubeVideoId: importPick(row, ['YouTube', 'YoutubeVideoId', 'youtubeVideoId']) || null,
    durationSec: Number(importPick(row, ['Süre', 'Sure', 'DurationSec', 'durationSec'])) || 0,
    category: parseCategory(importPick(row, ['Kategori', 'Category', 'category'])),
    rewardPoints: Number(importPick(row, ['Puan', 'RewardPoints', 'rewardPoints'])) || 0,
    isActive: parseBool(importPick(row, ['Durum', 'Aktif', 'IsActive', 'isActive']) || 'Aktif'),
  });
  const importValidateRow = (p) => {
    if (!p.textId) return 'Kod boş';
    if (!p.title) return 'Başlık boş';
    return null;
  };
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(API_BASE, { method: 'POST', headers: authHeaders(), body: JSON.stringify(p) });
      if (res.ok) return { ok: true };
      const body = await res.json().catch(() => null);
      return { ok: false, error: body?.message || body?.Message || `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ 'Kod': 'tr-product-101', 'Başlık': 'Örnek Eğitim', 'Açıklama': '', 'Kategori': 'Ürün', 'Süre': '120', 'Puan': '10', 'Durum': 'Aktif' }], null, 'egitim_sablon');
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, başlık veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Eğitimler"
        subtitle="Saha temsilcisine atanan eğitim videolarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
            <StatusBadge tone="ok">Aktif: {counts.activeCount}</StatusBadge>
            <StatusBadge tone="neutral">Pasif: {counts.inactiveCount}</StatusBadge>
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Eğitim</button>}
          </span>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.title}"</strong> eğitimini silmek istediğinize emin misiniz?</p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>Bu işlem geri alınamaz. Mobile'da bu eğitim bir sonraki sync'te kaybolacak.</p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} eğitim içe aktarıldı`); fetchData(); } }}
        title="Eğitim İçe Aktar"
        helpText="Zorunlu alanlar: Kod ve Başlık. Kategori: Oryantasyon/Ürün/Satış/Güvenlik/Uyum/Yetkinlik (boş ise Ürün). Durum: Aktif/Pasif."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}

export default TrainingsPage;
